import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import {
  SOCIAL_MEDIA_PLATFORMS,
  SOCIAL_MEDIA_NAMES,
  generateSocialMediaIconHtml,
} from "./social-media.data";
import { SocialMediaData, SocialMediaIcon } from "./social-media.interface";

@Component({
  selector: "app-social-media",
  templateUrl: "./social-media.component.html",
  styleUrls: ["./social-media.component.scss"],
  standalone: false,
})
export class SocialMediaComponent implements OnInit, OnChanges, OnDestroy {
  @Input() socialMediaData: SocialMediaData = {
    facebook: "",
    instagram: "",
    linkedin: "",
    twitter: "",
  };
  @Input() employeeData: Record<string, unknown> = { phone: "", email: "" };
  @Input() domain = "";
  @Input() brandColor = "#000000";
  @Input() selectedIcons: SocialMediaIcon[] = [];
  @Input() showAllSocialIcons = false;

  @Output() socialMediaChange = new EventEmitter<SocialMediaData>();
  @Output() employeeDataChange = new EventEmitter<Record<string, unknown>>();
  @Output() domainChange = new EventEmitter<string>();
  @Output() addCustomIconClick = new EventEmitter<void>();
  @Output() selectedIconsChange = new EventEmitter<SocialMediaIcon[]>();
  @Output() showAllSocialIconsChange = new EventEmitter<boolean>();

  allIcons: SocialMediaIcon[] = [];
  customIcons: SocialMediaIcon[] = [];
  private selectedPlatforms = new Set<string>();
  private isUpdatingFromParent = false;
  private emitTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.buildIcons();
  }

  ngOnChanges(): void {
    if (this.selectedIcons?.length) {
      this.isUpdatingFromParent = true;
      this.selectedPlatforms.clear();
      this.customIcons = this.selectedIcons.filter((icon) =>
        icon.platform.startsWith("custom_"),
      );
      this.selectedIcons
        .filter((icon) => !icon.platform.startsWith("custom_"))
        .forEach((icon) => this.selectedPlatforms.add(icon.platform));
      this.buildIcons();
      this.isUpdatingFromParent = false;
    }
  }

  ngOnDestroy(): void {
    if (this.emitTimeout) {
      clearTimeout(this.emitTimeout);
    }
  }

  private buildIcons(): void {
    const allPlatforms = [
      ...SOCIAL_MEDIA_PLATFORMS.DEFAULT,
      ...SOCIAL_MEDIA_PLATFORMS.ADDITIONAL,
    ];
    this.allIcons = allPlatforms.map((platform) =>
      this.createIcon(platform, this.selectedPlatforms.has(platform)),
    );
    if (!this.isUpdatingFromParent) {
      this.emitChanges();
    }
  }

  private updateIconUrl(platform: string, url: string): void {
    const icon = this.allIcons.find((i) => i.platform === platform);
    if (icon) {
      icon.url = url;
    }
  }

  toggleShowMore(): void {
    this.showAllSocialIcons = !this.showAllSocialIcons;
    this.showAllSocialIconsChange.emit(this.showAllSocialIcons);
  }

  private createIcon(platform: string, isDefault: boolean): SocialMediaIcon {
    return {
      name: SOCIAL_MEDIA_NAMES[platform as keyof typeof SOCIAL_MEDIA_NAMES],
      platform,
      imageUrl: generateSocialMediaIconHtml(platform, this.brandColor, "large"),
      alt: platform,
      selected: isDefault,
      url: this.getSocialMediaUrl(platform),
    };
  }

  toggleSocialIcon(icon: SocialMediaIcon): void {
    icon.selected = !icon.selected;

    if (icon.platform.startsWith("custom_")) {
      this.handleCustomIconToggle(icon);
    } else {
      this.handleStandardIconToggle(icon);
    }

    this.emitChanges();
  }

  private handleCustomIconToggle(icon: SocialMediaIcon): void {
    if (!icon.selected) {
      this.customIcons = this.customIcons.filter(
        (ci) => ci.platform !== icon.platform,
      );
    }
  }

  private handleStandardIconToggle(icon: SocialMediaIcon): void {
    if (icon.selected) {
      this.selectedPlatforms.add(icon.platform);
    } else {
      this.selectedPlatforms.delete(icon.platform);
    }
  }

  getSocialMediaUrl(platform: string): string {
    return this.socialMediaData[platform as keyof SocialMediaData] || "";
  }

  updateSocialMediaUrl(platform: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newUrl = input.value;
    (this.socialMediaData as unknown as Record<string, unknown>)[platform] =
      newUrl;
    this.updateIconUrl(platform, newUrl);
    this.debouncedEmitChanges();
  }

  updateCustomIconUrl(icon: SocialMediaIcon, event: Event): void {
    const input = event.target as HTMLInputElement;
    icon.url = input.value;
    this.debouncedEmitChanges();
  }

  showAddCustomIconForm(): void {
    this.addCustomIconClick.emit();
  }

  addCustomIcon(iconData: SocialMediaIcon): void {
    this.customIcons.push({
      name: iconData.name,
      platform: `custom_${Date.now()}`,
      imageUrl: iconData.imageUrl,
      alt: iconData.alt,
      selected: true,
      url: iconData.url,
    });
    this.emitChanges();
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      DOMPurify.sanitize(html ?? "", {
        ALLOWED_TAGS: [
          "a",
          "b",
          "br",
          "em",
          "i",
          "img",
          "p",
          "span",
          "strong",
          "table",
          "td",
          "tr",
          "div",
          "ul",
          "ol",
          "li",
        ],
      }),
    );
  }

  trackByPlatform(index: number, icon: SocialMediaIcon): string {
    return icon.platform;
  }

  private debouncedEmitChanges(): void {
    if (this.emitTimeout) {
      clearTimeout(this.emitTimeout);
    }
    this.emitTimeout = setTimeout(() => {
      this.emitChanges();
    }, 300);
  }

  private emitChanges(): void {
    if (this.isUpdatingFromParent) {
      return;
    }

    const selectedIcons = this.getAllSelectedIcons();

    this.socialMediaChange.emit(this.socialMediaData);
    this.employeeDataChange.emit(this.employeeData);
    this.domainChange.emit(this.domain);
    this.selectedIconsChange.emit(selectedIcons);
  }

  private getAllSelectedIcons(): SocialMediaIcon[] {
    return [...this.allIcons, ...this.customIcons].filter(
      (icon) => icon.selected,
    );
  }
}
